import React from 'react';
import {Chart as ChartJS, BarElement, CategoryScale, Legend, LineElement, LinearScale, PointElement, Tooltip} from 'chart.js';
import {Bar, Line} from 'react-chartjs-2';
import {Alert, Box, Divider, Skeleton, ToggleButton, ToggleButtonGroup, Typography, useTheme} from '@mui/material';
import {BarChartOutlined, ShowChart} from '@mui/icons-material';
import {alpha} from '@mui/material/styles';
import DashboardPaper from './DashboardPaper';
import ChartMetricSelect from './ChartMetricSelect';
import {dashboardMetrics, formatMetric} from './dashboardMetrics';
import useDashboardPreference from './useDashboardPreference';

ChartJS.register(BarElement, CategoryScale, Legend, LineElement, LinearScale, PointElement, Tooltip);

export default function PerformanceChart({data, currencyCode, error}) {
    const theme = useTheme();
    const [metrics, setMetrics] = useDashboardPreference('chartMetrics', ['visits']);
    const [chartType, setChartType] = useDashboardPreference('chartType', 'bar', ['bar', 'line']);
    const ChartComponent = chartType === 'bar' ? Bar : Line;
    const selectedMetrics = dashboardMetrics.filter(item => metrics.includes(item.key));
    const hasTraffic = selectedMetrics.some(metric => !metric.currency);
    const hasMoney = selectedMetrics.some(metric => metric.currency);
    const labels = data?.labels || [];
    const dark = theme.palette.mode === 'dark';
    const colors = {
        visits: theme.palette.primary.main, clicks: theme.palette.secondary.main,
        conversionsAll: dark ? '#b7a1ff' : '#7c3aed', revenue: dark ? '#f2b957' : '#b77915',
        cost: dark ? '#98a6b8' : '#64748b', profit: dark ? '#50d8d1' : '#0c8990'
    };
    const series = selectedMetrics.map(metric => {
        const values = labels.map((_, index) => {
            const value = Number((data?.[metric.chartKey || metric.key] || data?.[metric.key] || [])[index]);
            return Number.isFinite(value) ? value : 0;
        });
        return {metric, values, total: values.reduce((sum, value) => sum + value, 0)};
    });
    const chartData = {labels, datasets: series.map(({metric, values}) => ({
        label: metric.label, data: values, yAxisID: metric.currency ? 'money' : 'traffic',
        backgroundColor: chartType === 'bar'
            ? values.map(value => alpha(value < 0 ? theme.palette.error.main : colors[metric.key], 0.85))
            : colors[metric.key],
        hoverBackgroundColor: values.map(value => value < 0 ? theme.palette.error.main : colors[metric.key]),
        ...(chartType === 'bar' ? {borderRadius: 5, maxBarThickness: 28, borderSkipped: false} : {
            borderColor: colors[metric.key], borderWidth: 2, fill: false, tension: 0.2, cubicInterpolationMode: 'monotone',
            pointRadius: labels.length <= 14 ? 2 : 0, pointHoverRadius: 4,
            pointBackgroundColor: values.map(value => value < 0 ? theme.palette.error.main : colors[metric.key])
        })
    }))};
    const axis = (money, display, position) => ({
        display, position, beginAtZero: true, border: {display: false},
        grid: {color: alpha(theme.palette.text.secondary, 0.12), drawTicks: false, drawOnChartArea: position === 'left'},
        title: {display: hasTraffic && hasMoney, text: money ? `Amount (${currencyCode || 'EUR'})` : 'Traffic',
            color: theme.palette.text.secondary, font: {size: 11}},
        ticks: {padding: 8, maxTicksLimit: 6, color: theme.palette.text.secondary, font: {size: 11},
            precision: money ? undefined : 0,
            callback: value => formatMetric(value, {key: money ? 'revenue' : 'visits', currency: money}, currencyCode, true)}
    });
    const options = {
        responsive: true, maintainAspectRatio: false,
        interaction: {mode: 'index', intersect: false},
        plugins: {legend: {
            display: selectedMetrics.length > 1, position: 'bottom',
            labels: {color: theme.palette.text.secondary, usePointStyle: true, pointStyle: 'circle', boxWidth: 6, boxHeight: 6, padding: 14, font: {size: 11}},
            onClick: (_, item) => setMetrics(previous => previous.filter(key => key !== selectedMetrics[item.datasetIndex].key))
        }, tooltip: {
            backgroundColor: theme.palette.background.paper, titleColor: theme.palette.text.primary,
            bodyColor: theme.palette.text.secondary, borderColor: theme.palette.divider, borderWidth: 1,
            padding: 12, displayColors: selectedMetrics.length > 1,
            callbacks: {label: context => {
                const metric = selectedMetrics[context.datasetIndex];
                return `${metric.label}: ${formatMetric(context.parsed.y, metric, currencyCode)}`;
            }}
        }},
        scales: {
            x: {grid: {display: false}, border: {display: false}, ticks: {
                color: theme.palette.text.secondary, font: {size: 11}, maxRotation: 0, autoSkip: true, maxTicksLimit: 7}},
            traffic: axis(false, hasTraffic, 'left'),
            money: axis(true, hasMoney, hasTraffic ? 'right' : 'left')
        }
    };
    return (
        <DashboardPaper sx={{minWidth: 0, height: '100%', minHeight: 450, backgroundColor: 'background.darkBlue', display: 'flex', flexDirection: 'column'}}>
            <Box sx={{p: 2.5, display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 2}}>
                <Box>
                    <Typography component="h2" sx={{fontSize: 16, fontWeight: 600}}>Daily performance</Typography>
                    <Typography sx={{mt: 0.5, fontSize: 12, color: 'text.secondary'}}>Activity over the selected period</Typography>
                </Box>
                <Box sx={{display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1}}>
                    <ToggleButtonGroup exclusive size="small" value={chartType} aria-label="Chart type"
                        onChange={(_, value) => {
                            if (value) {
                                setChartType(value);
                            }
                        }}
                        sx={{height: 38, '& .MuiToggleButton-root': {px: 1, gap: 0.5, textTransform: 'none', fontSize: 12}}}>
                        <ToggleButton value="bar" aria-label="Bar chart"><BarChartOutlined sx={{fontSize: 18}}/>Bar</ToggleButton>
                        <ToggleButton value="line" aria-label="Line chart"><ShowChart sx={{fontSize: 18}}/>Line</ToggleButton>
                    </ToggleButtonGroup>
                    <ChartMetricSelect value={metrics} onChange={setMetrics}/>
                </Box>
            </Box>
            <Divider/>
            <Box sx={{p: 2.5, height: {xs: 260, sm: 295}, flexGrow: 1, minHeight: 0}}>
                {error ? <Alert severity="error">{error}</Alert> : data == null ?
                    <Skeleton variant="rounded" height="100%" aria-label="Loading daily performance"/> : !metrics.length ?
                        <Box sx={{height: '100%', display: 'grid', placeItems: 'center', color: 'text.secondary', fontSize: 14}}>Select one or more metrics to compare</Box> : !labels.length ?
                        <Box sx={{height: '100%', display: 'grid', placeItems: 'center', color: 'text.secondary', fontSize: 14}}>No results for this period</Box> :
                        <ChartComponent data={chartData} options={options} role="img"
                             aria-label={`Daily performance ${chartType} chart. ${series.map(({metric, total}) => `${metric.label}: ${formatMetric(total, metric, currencyCode)}`).join('. ')}`}/>}
            </Box>
            <Box sx={{px: 2.5, py: 1.75, borderTop: `1px solid ${theme.palette.divider}`, display: 'grid', gap: 1.5,
                gridTemplateColumns: selectedMetrics.length > 1 ? {xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(3, minmax(0, 1fr))'} : '1fr'}}>
                {series.map(({metric, total}) => (
                    <Box key={metric.key} sx={{display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 0.5}}>
                        <Typography sx={{fontSize: 12, color: 'text.secondary'}}>Total {metric.label.toLowerCase()}</Typography>
                        <Typography sx={{fontSize: 13, fontWeight: 600}}>{data == null || error ? '—' : formatMetric(total, metric, currencyCode)}</Typography>
                    </Box>
                ))}
            </Box>
        </DashboardPaper>
    );
}
